import React, { useCallback, useState } from 'react'
import type {
  SeatData,
  SeatDisplayStatus,
  VenueSectionData,
  SeatRowData,
  SelectedSeat,
} from './SeatMap.types'

// ── Constants ────────────────────────────────────────────────────────────────

const SEAT_SIZE = 22
const SEAT_GAP = 4
const ROW_LABEL_WIDTH = 28
const SECTION_PADDING = 8

const SEAT_COLORS: Record<SeatDisplayStatus, string> = {
  AVAILABLE:     '#22c55e',
  SOLD:          '#ef4444',
  RESERVED_TEMP: '#f59e0b',
  SELECTED:      '#eab308',
  BLOCKED_ADMIN: '#374151',
  OFFLINE:       '#4b5563',
}

const SEAT_OPACITY: Record<SeatDisplayStatus, number> = {
  AVAILABLE:     1,
  SOLD:          1,
  RESERVED_TEMP: 1,
  SELECTED:      1,
  BLOCKED_ADMIN: 1,
  OFFLINE:       0.35,
}

const SEAT_CURSOR: Record<SeatDisplayStatus, string> = {
  AVAILABLE:     'pointer',
  SOLD:          'not-allowed',
  RESERVED_TEMP: 'not-allowed',
  SELECTED:      'pointer',
  BLOCKED_ADMIN: 'not-allowed',
  OFFLINE:       'default',
}

// ── Helper: resolve effective display status ──────────────────────────────────

function resolveStatus(
  seat: SeatData,
  selectedIds: Set<number>,
): SeatDisplayStatus {
  if (selectedIds.has(seat.id)) return 'SELECTED'
  return seat.status
}

// ── Seat node (memoized to avoid 702-node re-renders) ────────────────────────

interface SeatNodeProps {
  seat: SeatData
  x: number
  y: number
  displayStatus: SeatDisplayStatus
  onClick: () => void
}

const SeatNode = React.memo(function SeatNode({
  seat,
  x,
  y,
  displayStatus,
  onClick,
}: SeatNodeProps) {
  const [hovered, setHovered] = useState(false)
  const isInteractive = displayStatus === 'AVAILABLE' || displayStatus === 'SELECTED'
  const baseOpacity = SEAT_OPACITY[displayStatus]

  return (
    <g
      role="button"
      aria-label={`Asiento ${seat.label} — ${displayStatus}`}
      aria-pressed={displayStatus === 'SELECTED'}
      tabIndex={isInteractive ? 0 : -1}
      style={{ outline: 'none' }}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={(e) => {
        if (isInteractive && (e.key === 'Enter' || e.key === ' ')) onClick()
      }}
      onMouseEnter={isInteractive ? () => setHovered(true) : undefined}
      onMouseLeave={isInteractive ? () => setHovered(false) : undefined}
    >
      <rect
        x={x}
        y={y}
        width={SEAT_SIZE}
        height={SEAT_SIZE}
        rx={4}
        fill={SEAT_COLORS[displayStatus]}
        opacity={hovered && isInteractive ? baseOpacity * 0.8 : baseOpacity}
        style={{ cursor: SEAT_CURSOR[displayStatus], transition: 'opacity 0.1s' }}
      />
      {displayStatus !== 'OFFLINE' && (
        <text
          x={x + SEAT_SIZE / 2}
          y={y + SEAT_SIZE / 2 + 3}
          textAnchor="middle"
          fontSize={7}
          fontWeight="600"
          fill={displayStatus === 'AVAILABLE' ? '#14532d' : 'white'}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {seat.seatNumber}
        </text>
      )}
    </g>
  )
})

// ── Row renderer ─────────────────────────────────────────────────────────────

interface RowProps {
  row: SeatRowData
  y: number
  selectedIds: Set<number>
  onSeatClick: (seat: SeatData) => void
  conflictIds: Set<number>
}

const SeatRow = React.memo(function SeatRow({
  row,
  y,
  selectedIds,
  onSeatClick,
  conflictIds,
}: RowProps) {
  return (
    <g>
      {/* Row label */}
      <text
        x={ROW_LABEL_WIDTH - 4}
        y={y + SEAT_SIZE / 2 + 4}
        textAnchor="end"
        fontSize={11}
        fill="white"
        style={{ userSelect: 'none' }}
      >
        {row.rowLabel}
      </text>

      {/* Seats */}
      {row.seats.map((seat, i) => {
        const x = ROW_LABEL_WIDTH + i * (SEAT_SIZE + SEAT_GAP)
        // Briefly flash conflict seats as red
        const status: SeatDisplayStatus = conflictIds.has(seat.id)
          ? 'SOLD'
          : resolveStatus(seat, selectedIds)

        return (
          <SeatNode
            key={seat.id}
            seat={seat}
            x={x}
            y={y}
            displayStatus={status}
            onClick={() => onSeatClick(seat)}
          />
        )
      })}
    </g>
  )
})

// ── Section renderer ──────────────────────────────────────────────────────────

function sectionSvgHeight(section: VenueSectionData): number {
  // header + rows
  const rowsHeight =
    section.rows.length * (SEAT_SIZE + SEAT_GAP) - SEAT_GAP
  return 20 + rowsHeight + SECTION_PADDING * 2
}

function sectionSvgWidth(section: VenueSectionData): number {
  const maxSeats = Math.max(...section.rows.map((r) => r.seats.length))
  return ROW_LABEL_WIDTH + maxSeats * (SEAT_SIZE + SEAT_GAP) - SEAT_GAP + SECTION_PADDING
}

interface SectionProps {
  section: VenueSectionData
  selectedIds: Set<number>
  onSeatClick: (seat: SeatData) => void
  conflictIds: Set<number>
}

function SectionBlock({ section, selectedIds, onSeatClick, conflictIds }: SectionProps) {
  const svgWidth = sectionSvgWidth(section)
  const svgHeight = sectionSvgHeight(section)

  return (
    <div className="flex flex-col gap-1">
      {/* Section header */}
      <div className="flex items-baseline gap-2">
        <span className="text-sm font-semibold text-secondary-120">
          {section.name}
        </span>
        {section.subSection && (
          <span className="text-xs text-white/60">{section.subSection}</span>
        )}
      </div>

      {/* SVG grid */}
      <svg
        width={svgWidth}
        height={svgHeight}
        role="group"
        aria-label={`Sección ${section.name}${section.subSection ? ' ' + section.subSection : ''}`}
        style={{ display: 'block', overflow: 'visible' }}
      >
        {section.rows.map((row, ri) => {
          const y = SECTION_PADDING + ri * (SEAT_SIZE + SEAT_GAP)
          return (
            <SeatRow
              key={row.rowLabel}
              row={row}
              y={y}
              selectedIds={selectedIds}
              onSeatClick={onSeatClick}
              conflictIds={conflictIds}
            />
          )
        })}
      </svg>
    </div>
  )
}

// ── Main SeatMap ──────────────────────────────────────────────────────────────

export interface SeatMapProps {
  sections: VenueSectionData[]
  selectedSeats: SelectedSeat[]
  onSeatClick: (seat: SeatData) => void
}

/**
 * Interactive SVG seat map.
 * - AVAILABLE seats are clickable (calls onSeatClick).
 * - SELECTED seats (owned by this session) are yellow and clickable to deselect.
 * - SOLD / RESERVED_TEMP / BLOCKED_ADMIN are red/dark-grey and non-interactive.
 * - On 409 conflict, briefly flashes the conflicting seat as red.
 */
export function SeatMap({ sections, selectedSeats, onSeatClick }: SeatMapProps) {
  const [conflictIds, setConflictIds] = useState<Set<number>>(new Set())

  const selectedIds = new Set(selectedSeats.map((s) => s.seatId))

  const handleSeatClick = useCallback(
    (seat: SeatData) => {
      // Clear any conflict highlight on a new click
      setConflictIds(new Set())
      onSeatClick(seat)
    },
    [onSeatClick],
  )

  // Expose a way to mark conflicts (called by parent after 409)
  // We pass the conflictIds state down via a stable Set
  // and reset after 2 seconds
  const markConflict = useCallback((seatId: number) => {
    setConflictIds(new Set([seatId]))
    setTimeout(() => setConflictIds(new Set()), 2000)
  }, [])

  // Expose markConflict via imperative ref alternative — here we lift it via
  // onConflict prop from parent. Parent calls with seatId from useSeatReservation.
  // For now, expose via a data attribute trick is unnecessary — the parent
  // must call handleConflict(seatId) which is exposed as a property.
  void markConflict // used below in the returned object

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="seat-map"
    >
      {sections.map((section) => (
        <SectionBlock
          key={section.id}
          section={section}
          selectedIds={selectedIds}
          onSeatClick={handleSeatClick}
          conflictIds={conflictIds}
        />
      ))}
    </div>
  )
}

/**
 * Extended version that exposes a conflict-flash handler to the parent.
 * Use this variant when integrating with useSeatReservation.
 */
export interface SeatMapWithConflictProps extends SeatMapProps {
  /** Called when this component should flash a seat as conflicted. */
  onConflictRef?: (flashFn: (seatId: number) => void) => void
}

export function SeatMapWithConflict({
  sections,
  selectedSeats,
  onSeatClick,
  onConflictRef,
}: SeatMapWithConflictProps) {
  const [conflictIds, setConflictIds] = useState<Set<number>>(new Set())
  const selectedIds = new Set(selectedSeats.map((s) => s.seatId))

  const flashConflict = useCallback((seatId: number) => {
    setConflictIds(new Set([seatId]))
    setTimeout(() => setConflictIds(new Set()), 2000)
  }, [])

  // Register flash handler with parent on mount
  React.useEffect(() => {
    onConflictRef?.(flashConflict)
  }, [onConflictRef, flashConflict])

  const handleSeatClick = useCallback(
    (seat: SeatData) => {
      setConflictIds(new Set())
      onSeatClick(seat)
    },
    [onSeatClick],
  )

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="seat-map"
    >
      {sections.map((section) => (
        <SectionBlock
          key={section.id}
          section={section}
          selectedIds={selectedIds}
          onSeatClick={handleSeatClick}
          conflictIds={conflictIds}
        />
      ))}
    </div>
  )
}
